import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Card,
  Typography,
  FormControlLabel,
  Checkbox,
  Button,
  makeStyles,
  useMediaQuery,
  Box,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import cx from 'classnames';
import Terms from './Terms/Terms';
import ButtonNavigation from '../../ButtonNavigation/ButtonNavigation';
import sanitizer from '../../../services/sanitize-service';
import allActions from '../../../store/actions';
import buildPATHS from '../../../utils/paths';
import { getScrollbarStyles } from '../../../utils/getStyles/getStyles';
import styles from './TermsForm.module.scss';
import { getTranslation, hasAllTruthyValues } from '../../../utils/functions';
import {
  termsCurrentStepSelector,
  termsStepsSelector,
} from '../../../store/selectors/termsConfigSelectors';
import {
  introSelector,
  textsOverwritingsSelector,
} from '../../../store/selectors/globalConfigSelectors';
import LodashUtils from '../../../utils/lodashUtils';

const useTermsFormStyles = (isMobileView) =>
  makeStyles((theme) => ({
    card: {
      padding: isMobileView ? '28px 20px' : '28px 40px',
    },
    cardTitle: {
      wordBreak: 'break-word',
      marginTop: theme.custom.margin.m20,
      marginBottom: theme.custom.margin.m20 * 2,
    },
    contentWrapper: {
      ...(isMobileView ? { height: 500 } : {}),
      display: 'flex',
      flexDirection: 'column',
      overflow: isMobileView ? 'hidden' : 'auto',
      ...getScrollbarStyles(theme),
    },
    contentWrapperOpen: {
      overflow: 'visible',
      height: 'auto',
    },
    title: {
      position: 'absolute',
      left: 12,
      top: 0,
      margin: 0,
      transform: 'translateY(-50%)',
      padding: 4,
      backgroundColor: theme.palette.common.white,
      zIndex: 2,
      fontWeight: 500,
      fontSize: theme.custom.fontSize.subsection,
    },
    titleMobile: {
      marginTop: 0,
      marginBottom: 16,
      fontWeight: 500,
      fontSize: theme.custom.fontSize.subsection,
    },
    board: {
      position: 'relative',
      marginTop: isMobileView ? 32 : 40,
      width: '100%',
      border: isMobileView ? 'none' : `1px solid ${theme.palette.grey[300]}`,
      height: '100%',
      minHeight: 200,
      borderRadius: 5,
    },
    separator: {
      display: isMobileView ? 'none' : 'block',
    },
    expandButton: {
      padding: '30px 0 32px 0',
      borderBottom: `1px solid ${theme.palette.grey[300]}`,
    },
    checkboxField: {
      marginTop: 12,
      marginBottom: 12,
      alignItems: 'flex-start',
    },
    checkbox: {
      paddingTop: 0,
      color: theme.palette.text.primary,
    },
    expandButtonContainer: {
      padding: '32px 0',
      borderBottom: `1px solid ${theme.palette.grey[300]}`,
    },
  }));

const TermsForm = () => {
  const PATHS = buildPATHS();

  const [areTermsRead, setTermsRead] = useState(false);
  const [termsChecked, setTermsChecked] = useState({});
  const [areTermsOpen, setTermsOpen] = useState(false);

  const { currentStep, steps, textsOverwritings, intro } = useSelector((state) => ({
    // termsConfig state
    currentStep: termsCurrentStepSelector(state),
    steps: termsStepsSelector(state),
    // globalConfig state
    textsOverwritings: textsOverwritingsSelector(state),
    intro: introSelector(state),
  }));

  const { t } = useTranslation();

  const history = useHistory();
  const dispatch = useDispatch();
  const isMobileView = useMediaQuery((theme) => theme.breakpoints.down('xs'));
  const classes = useTermsFormStyles(isMobileView)();

  const currentTermConfig = steps[currentStep];
  const checkboxConfig = currentTermConfig.fieldConfigs
    ? currentTermConfig.fieldConfigs
    : [currentTermConfig];

  const fieldToUse = currentTermConfig.fieldConfigs ? 'fieldCode' : 'code';

  useEffect(() => {
    const defaultTermsCheckedArr = checkboxConfig.map((config) => {
      return { [config[fieldToUse]]: false };
    });

    const defaultTermsCheckedObj = Object.assign({}, ...defaultTermsCheckedArr);
    setTermsChecked(defaultTermsCheckedObj);
  }, [currentTermConfig]);

  const toggleChecked = (event) => {
    setTermsChecked({ ...termsChecked, [event.target.name]: event.target.checked });
  };
  const toggleTermsOpen = () => setTermsOpen((prevState) => !prevState);

  const isButtonDisabled = () => {
    if (!currentTermConfig.fullViewRequired) {
      return false;
    }
    return !hasAllTruthyValues(termsChecked);
  };

  const getAccepetedTermsToSave = () => {
    return LodashUtils.map(termsChecked, (value, key) => ({ [fieldToUse]: key, accepted: value }));
  };
  const saveTerms = () => {
    const acceptedTerms = getAccepetedTermsToSave();
    dispatch(allActions.termsConfigActions.setTermsAcceptance(acceptedTerms));
  };

  const redirectToRegisterForm = () => {
    dispatch(allActions.appConfigActions.setAccessToRegister(true));
    history.push(PATHS.register);
  };

  const resetAndGoNextStep = () => {
    setTermsChecked({});
    setTermsRead(false);
    setTermsOpen(false);
    dispatch(allActions.termsConfigActions.goNextStep());
  };

  const handleNextStepClick = () => {
    saveTerms();
  
    if (currentStep >= steps.length - 1) {
      redirectToRegisterForm();
    } else {
      resetAndGoNextStep();
    }
  };

  const handleExpandClick = () => {
    setTermsRead(true);
    toggleTermsOpen();
  };

  const ExpandButton = (
    <>
      {isMobileView && (
        <Box display="flex" justifyContent="center" className={classes.expandButtonContainer}>
          <Button onClick={handleExpandClick} color="primary">
            {areTermsOpen
              ? getTranslation(textsOverwritings, 'BUTTON_SHOW_LESS') || t('BUTTON_SHOW_LESS')
              : getTranslation(textsOverwritings, 'BUTTON_SHOW_MORE') || t('BUTTON_SHOW_MORE')}
          </Button>
        </Box>
      )}
    </>
  );

  const renderCheckboxes = () => {
    return checkboxConfig.map((config) => {
      return (
        <FormControlLabel
          disabled={!areTermsRead}
          className={classes.checkboxField}
          control={
            <Checkbox
              className={classes.checkbox}
              color="primary"
              checked={!!termsChecked[config[fieldToUse]]}
              onChange={toggleChecked}
              name={config[fieldToUse]}
            />
          }
          label={
            <div
              dangerouslySetInnerHTML={{ __html: sanitizer(getTranslation(config.checkBoxLabel)) }}
            />
          }
        />
      );
    });
  };

  return (
    <Card className={classes.card}>
      <Box
        className={cx(classes.contentWrapper, {
          [classes.contentWrapperOpen]: areTermsOpen,
        })}
      >
        <Typography className={classes.cardTitle} variant="h4">
          {getTranslation(intro.title)}
        </Typography>
        <Typography
          dangerouslySetInnerHTML={{ __html: sanitizer(getTranslation(intro.introductionText)) }}
        />

        <Box className={classes.board}>
          <Typography
            variant="h4"
            className={cx(
              { [classes.title]: !isMobileView },
              { [classes.titleMobile]: isMobileView },
            )}
          >
            {getTranslation(currentTermConfig.title)}
          </Typography>
          <Box className={cx(classes.separator, styles.separator)} />
          <Terms termContent={getTranslation(currentTermConfig.text)} setTermsRead={setTermsRead} />
        </Box>
      </Box>

      {ExpandButton}
      {renderCheckboxes()}

      <Box component="section" display="flex" justifyContent="flex-end">
        <ButtonNavigation onClick={handleNextStepClick} disabled={isButtonDisabled()} next>
          {getTranslation(textsOverwritings, 'BUTTON_CONTINUE') || t('BUTTON_CONTINUE')}
        </ButtonNavigation>
      </Box>
    </Card>
  );
};

export default TermsForm;
